import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ButtonWithSpinner from "../ButtonWithSpinner";
import TaskUserAutocomplete from "../TaskUserAutocomplete";
import TaskContactAutocomplete from "../TaskContactAutocomplete";
import {
  TASK_TYPE_OPTIONS,
  TASK_PRIORITY_OPTIONS,
  shouldClearContactSelection,
  buildCreateTaskPayload,
  isTaskFormValid
} from "../../pages/Tasks/taskUtils";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  }
}));

// Monta os valores iniciais para ESTA abertura do modal — nunca reaproveita
// nem muda um objeto compartilhado entre aberturas. Sem initialTicketContext,
// o comportamento é idêntico ao da criação avulsa de sempre (INTERNAL,
// tudo em branco).
const buildInitialValues = initialTicketContext => {
  if (initialTicketContext) {
    return {
      type: "CONTACT",
      title: "",
      description: "",
      priority: "medium",
      dueDate: "",
      isRecurring: false,
      recurrenceCount: 1,
      assignedUserId: null,
      contactId: initialTicketContext.contact.id,
      ticketId: initialTicketContext.ticketId
    };
  }

  return {
    type: "INTERNAL",
    title: "",
    description: "",
    priority: "medium",
    dueDate: "",
    isRecurring: false,
    recurrenceCount: 1,
    assignedUserId: null,
    contactId: null,
    ticketId: null
  };
};

// Modal de criação de tarefa. Cada abertura remonta o componente (o pai
// controla via render condicional), então fechar e reabrir nunca reutiliza
// estado de uma tarefa/contexto anterior — cada `useState`/`buildInitialValues`
// roda do zero.
//
// `initialTicketContext`, quando informado pela tela de atendimento, é um
// objeto mínimo e explícito: `{ ticketId, ticketUuid, contact: {id, name,
// number} }` — nunca o objeto completo do ticket. Trava type=CONTACT e o
// contato selecionado; ticketId nunca vira campo editável no formulário,
// só entra no payload via buildCreateTaskPayload.
const TaskModal = ({ open, onClose, onSuccess, initialTicketContext }) => {
  const classes = useStyles();
  const isTicketContextLocked = Boolean(initialTicketContext);
  const initialValues = buildInitialValues(initialTicketContext);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedContact, setSelectedContact] = useState(
    initialTicketContext ? initialTicketContext.contact : null
  );
  const [saving, setSaving] = useState(false);

  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const TaskSchema = Yup.object().shape({
    type: Yup.string()
      .oneOf(TASK_TYPE_OPTIONS, i18n.t("tasks.modal.errors.typeInvalid"))
      .required(i18n.t("tasks.modal.errors.typeRequired")),
    title: Yup.string()
      .trim()
      .min(3, i18n.t("tasks.modal.errors.titleTooShort"))
      .max(255, i18n.t("tasks.modal.errors.titleTooLong"))
      .required(i18n.t("tasks.modal.errors.titleRequired")),
    description: Yup.string().max(5000, i18n.t("tasks.modal.errors.descriptionTooLong")),
    priority: Yup.string().oneOf(TASK_PRIORITY_OPTIONS),
    dueDate: Yup.string()
      .nullable()
      .when("isRecurring", {
        is: true,
        then: Yup.string().required(i18n.t("tasks.modal.errors.recurrenceRequiresDueDate"))
      }),
    isRecurring: Yup.boolean(),
    recurrenceCount: Yup.number().when("isRecurring", {
      is: true,
      then: Yup.number()
        .integer(i18n.t("tasks.modal.errors.recurrenceCountInvalid"))
        .min(1, i18n.t("tasks.modal.errors.recurrenceCountInvalid"))
        .max(60, i18n.t("tasks.modal.errors.recurrenceCountInvalid"))
        .required(i18n.t("tasks.modal.errors.recurrenceCountInvalid"))
    }),
    assignedUserId: Yup.number()
      .nullable()
      .required(i18n.t("tasks.modal.errors.assignedUserRequired")),
    contactId: Yup.number()
      .nullable()
      .when("type", {
        is: "CONTACT",
        then: Yup.number()
          .nullable()
          .required(i18n.t("tasks.modal.errors.contactRequired"))
      }),
    ticketId: isTicketContextLocked
      ? Yup.number().positive().integer().required()
      : Yup.number().nullable()
  });

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSaveTask = async (values, actions) => {
    // Segunda guarda antes do envio, além do Yup do Formik — defesa em
    // profundidade, mesma regra pura testada em taskUtils.js.
    if (!isTaskFormValid(values)) {
      actions.setSubmitting(false);
      return;
    }

    setSaving(true);

    try {
      const payload = buildCreateTaskPayload(values);
      await api.post("/tasks", payload);

      if (!isMounted.current) return;

      toast.success(i18n.t("tasks.modal.success"));
      actions.setSubmitting(false);
      setSaving(false);

      if (typeof onSuccess === "function") onSuccess();
      onClose();
    } catch (err) {
      if (!isMounted.current) return;
      actions.setSubmitting(false);
      setSaving(false);
      toastError(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      aria-labelledby="task-modal-title"
      disableBackdropClick={saving}
      disableEscapeKeyDown={saving}
    >
      <DialogTitle id="task-modal-title">{i18n.t("tasks.modal.titleCreate")}</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={TaskSchema}
        onSubmit={(values, actions) => {
          handleSaveTask(values, actions);
        }}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => (
          <Form>
            <DialogContent dividers className={classes.root}>
              <Grid container spacing={2}>
                {isTicketContextLocked && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("tasks.modal.linkedToTicket", {
                        uuid: initialTicketContext.ticketUuid
                          ? initialTicketContext.ticketUuid.slice(0, 8)
                          : ""
                      })}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <FormControl variant="outlined" size="small" fullWidth disabled={isTicketContextLocked}>
                    <InputLabel id="task-type-label">
                      {i18n.t("tasks.modal.type")}
                    </InputLabel>
                    <Select
                      labelId="task-type-label"
                      label={i18n.t("tasks.modal.type")}
                      value={values.type}
                      disabled={isTicketContextLocked}
                      onChange={e => {
                        const newType = e.target.value;
                        setFieldValue("type", newType);
                        if (shouldClearContactSelection(newType)) {
                          setFieldValue("contactId", null);
                          setSelectedContact(null);
                        }
                      }}
                    >
                      <MenuItem value="INTERNAL">{i18n.t("tasks.type.INTERNAL")}</MenuItem>
                      <MenuItem value="CONTACT">{i18n.t("tasks.type.CONTACT")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={Boolean(values.isRecurring)}
                        onChange={e => setFieldValue("isRecurring", e.target.checked)}
                      />
                    }
                    label={i18n.t("tasks.modal.monthlyRecurrence")}
                  />
                </Grid>

                {values.isRecurring && (
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      name="recurrenceCount"
                      type="number"
                      label={i18n.t("tasks.modal.recurrenceCount")}
                      variant="outlined"
                      size="small"
                      fullWidth
                      required
                      inputProps={{ min: 1, max: 60, step: 1 }}
                      error={touched.recurrenceCount && Boolean(errors.recurrenceCount)}
                      helperText={
                        (touched.recurrenceCount && errors.recurrenceCount) ||
                        i18n.t("tasks.modal.recurrenceCountHelp")
                      }
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <FormControl variant="outlined" size="small" fullWidth>
                    <InputLabel id="task-priority-label">
                      {i18n.t("tasks.modal.priority")}
                    </InputLabel>
                    <Select
                      labelId="task-priority-label"
                      label={i18n.t("tasks.modal.priority")}
                      value={values.priority}
                      onChange={e => setFieldValue("priority", e.target.value)}
                    >
                      {TASK_PRIORITY_OPTIONS.map(priority => (
                        <MenuItem key={priority} value={priority}>
                          {i18n.t(`tasks.priority.${priority}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="title"
                    label={i18n.t("tasks.modal.title")}
                    variant="outlined"
                    size="small"
                    fullWidth
                    required
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="description"
                    label={i18n.t("tasks.modal.description")}
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    name="dueDate"
                    type="date"
                    label={i18n.t("tasks.modal.dueDate")}
                    variant="outlined"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={touched.dueDate && Boolean(errors.dueDate)}
                    helperText={touched.dueDate && errors.dueDate}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TaskUserAutocomplete
                    label={i18n.t("tasks.modal.assignedUser")}
                    required
                    value={selectedUser}
                    error={touched.assignedUserId && Boolean(errors.assignedUserId)}
                    helperText={touched.assignedUserId && errors.assignedUserId}
                    onChange={newValue => {
                      setSelectedUser(newValue);
                      setFieldValue("assignedUserId", newValue ? newValue.id : null);
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TaskContactAutocomplete
                    label={i18n.t("tasks.modal.contact")}
                    required={values.type === "CONTACT"}
                    disabled={isTicketContextLocked || values.type !== "CONTACT"}
                    value={selectedContact}
                    error={touched.contactId && Boolean(errors.contactId)}
                    helperText={
                      (touched.contactId && errors.contactId) ||
                      (isTicketContextLocked ? i18n.t("tasks.modal.contactLockedByTicket") : "")
                    }
                    onChange={newValue => {
                      setSelectedContact(newValue);
                      setFieldValue("contactId", newValue ? newValue.id : null);
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleClose}
                color="secondary"
                disabled={isSubmitting}
                variant="outlined"
              >
                {i18n.t("tasks.modal.cancel")}
              </Button>
              <ButtonWithSpinner
                type="submit"
                color="primary"
                variant="contained"
                loading={isSubmitting}
              >
                {i18n.t("tasks.modal.save")}
              </ButtonWithSpinner>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default TaskModal;
